/**
 * The frozen cumulative surah offset table, and `ayahId ⇄ (surah, ayah)` both
 * ways. SPEC §3.1, Layer 2.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS A FROZEN CONTRACT. See the banner in `types.ts`.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The numbering is Hafs 'an 'Asim, Kufan count: 114 surahs, 6236 ayat. Both
 * tables have 115 entries, indices 0..114, with index 0 a padding slot that is
 * never read — so the arrays are 1-indexed and `AYAH_OFFSET[2] === 7` reads the
 * way SPEC §3.1's worked example writes it.
 *
 * `AYAH_OFFSET[s]` is the number of ayat in surahs 1..s-1, i.e. the ayahId
 * immediately BEFORE the first ayah of surah `s`. So:
 *
 *     ayahId(s, a) = AYAH_OFFSET[s] + a
 *
 * al-Fatiha is ayahId 1..7, so `AYAH_OFFSET[1] === 0` and `AYAH_OFFSET[2] === 7`.
 * There is no sentinel entry for a 115th surah; `TOTAL_AYAT` plays that role
 * and the binary search in `surahOf` is written to not need one.
 *
 * Both tables are verified against the vendored Tanzil text by
 * `scripts/build-anchor-fixture.ts` and asserted in `anchor.spec.ts`.
 */

import { AnchorRefError, SURAH_COUNT, TOTAL_AYAT, type AyahId, type Location } from './types';

/**
 * Ayat per surah. Index 0 is padding. Sum === TOTAL_AYAT === 6236.
 *
 * The load-bearing values, the ones a wrong table gets wrong: 2 → 286,
 * 9 → 129, 26 → 227, 36 → 83, 55 → 78, 78 → 40, 114 → 6.
 */
// prettier-ignore
export const SURAH_AYAH_COUNT: readonly number[] = Object.freeze([
	0, // padding — surah 0 does not exist
	7, 286, 200, 176, 120, 165, 206, 75, 129, 109, // 1..10
	123, 111, 43, 52, 99, 128, 111, 110, 98, 135, // 11..20
	112, 78, 118, 64, 77, 227, 93, 88, 69, 60, // 21..30
	34, 30, 73, 54, 45, 83, 182, 88, 75, 85, // 31..40
	54, 53, 89, 59, 37, 35, 38, 29, 18, 45, // 41..50
	60, 49, 62, 55, 78, 96, 29, 22, 24, 13, // 51..60
	14, 11, 11, 18, 12, 12, 30, 52, 52, 44, // 61..70
	28, 28, 20, 56, 40, 31, 50, 40, 46, 42, // 71..80
	29, 19, 36, 25, 22, 17, 19, 26, 30, 20, // 81..90
	15, 21, 11, 8, 8, 19, 5, 8, 8, 11, // 91..100
	11, 8, 3, 9, 5, 4, 7, 3, 6, 3, // 101..110
	5, 4, 5, 6 // 111..114
]);

/**
 * THE FROZEN 115-ENTRY CUMULATIVE OFFSET TABLE.
 *
 * `AYAH_OFFSET[s] = Σ SURAH_AYAH_COUNT[1..s-1]`. Index 0 is padding.
 * `anchor.spec.ts` re-derives this from `SURAH_AYAH_COUNT` and asserts equality,
 * so a typo in either table fails the build rather than silently repointing
 * every remark in a surah.
 */
// prettier-ignore
export const AYAH_OFFSET: readonly number[] = Object.freeze([
	0, // padding — surah 0 does not exist
	0, 7, 293, 493, 669, 789, 954, 1160, 1235, 1364, // 1..10
	1473, 1596, 1707, 1750, 1802, 1901, 2029, 2140, 2250, 2348, // 11..20
	2483, 2595, 2673, 2791, 2855, 2932, 3159, 3252, 3340, 3409, // 21..30
	3469, 3503, 3533, 3606, 3660, 3705, 3788, 3970, 4058, 4133, // 31..40
	4218, 4272, 4325, 4414, 4473, 4510, 4545, 4583, 4612, 4630, // 41..50
	4675, 4735, 4784, 4846, 4901, 4979, 5075, 5104, 5126, 5150, // 51..60
	5163, 5177, 5188, 5199, 5217, 5229, 5241, 5271, 5323, 5375, // 61..70
	5419, 5447, 5475, 5495, 5551, 5591, 5622, 5672, 5712, 5758, // 71..80
	5800, 5829, 5848, 5884, 5909, 5931, 5948, 5967, 5993, 6023, // 81..90
	6043, 6058, 6079, 6090, 6098, 6106, 6125, 6130, 6138, 6146, // 91..100
	6157, 6168, 6176, 6179, 6188, 6193, 6197, 6204, 6207, 6213, // 101..110
	6216, 6221, 6225, 6230 // 111..114
]);

/** `true` iff `n` is an integer in 1..114. */
export function isSurah(n: unknown): n is number {
	return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= SURAH_COUNT;
}

/** `true` iff `id` is an integer in 1..6236. */
export function isValidAyahId(id: unknown): id is AyahId {
	return typeof id === 'number' && Number.isInteger(id) && id >= 1 && id <= TOTAL_AYAT;
}

/** `true` iff `(surah, ayah)` names an ayah that exists in the Hafs count. */
export function isValidLocation(surah: unknown, ayah: unknown): boolean {
	if (!isSurah(surah)) return false;
	if (typeof ayah !== 'number' || !Number.isInteger(ayah) || ayah < 1) return false;
	return ayah <= ayahCountOf(surah);
}

/** Ayat in `surah`. Throws on an out-of-range surah. */
export function ayahCountOf(surah: number): number {
	if (!isSurah(surah)) {
		throw new AnchorRefError('surah_out_of_range', String(surah), `Surah ${surah} does not exist`);
	}
	// Safe: isSurah has constrained surah to 1..114 and the table has 115 entries.
	return SURAH_AYAH_COUNT[surah] as number;
}

/** The number of ayat before surah `s` — i.e. `ayahId` of its ayah 0. */
export function offsetOf(surah: number): number {
	if (!isSurah(surah)) {
		throw new AnchorRefError('surah_out_of_range', String(surah), `Surah ${surah} does not exist`);
	}
	return AYAH_OFFSET[surah] as number;
}

/** `(surah, ayah) → ayahId ∈ 1..6236`. Throws if the ayah does not exist. */
export function ayahIdOf(surah: number, ayah: number): AyahId {
	if (!isValidLocation(surah, ayah)) {
		throw new AnchorRefError(
			'ayah_out_of_range',
			`${surah}:${ayah}`,
			`Ayah ${surah}:${ayah} does not exist in the Hafs count`
		);
	}
	return (AYAH_OFFSET[surah] as number) + ayah;
}

/**
 * `ayahId → surah`. Binary search over `AYAH_OFFSET[1..114]` for the last surah
 * whose offset is strictly less than `id`.
 */
export function surahOf(id: AyahId): number {
	if (!isValidAyahId(id)) {
		throw new AnchorRefError('ayah_id_out_of_range', String(id), `ayahId ${id} is not in 1..6236`);
	}
	let lo = 1;
	let hi = SURAH_COUNT;
	while (lo < hi) {
		const mid = (lo + hi + 1) >> 1;
		if ((AYAH_OFFSET[mid] as number) < id) lo = mid;
		else hi = mid - 1;
	}
	return lo;
}

/** `ayahId → ayah within its surah`, 1-based. */
export function ayahOf(id: AyahId): number {
	return id - (AYAH_OFFSET[surahOf(id)] as number);
}

/** `ayahId → {surah, ayah}`. The inverse of `ayahIdOf`. */
export function locationOf(id: AyahId): Location {
	const surah = surahOf(id);
	return { surah, ayah: id - (AYAH_OFFSET[surah] as number) };
}
